/**
 * recalProgressBar — the truth layer behind the recalibration status bar.
 *
 * Founder, 2026-09-02: "instead of an inaccurate time count down when you
 * recal, we should have a status bar. We need to find the right anchors for
 * this and then smooth the bar out. Similar to how the liquid loader for canva
 * upload works."
 *
 * ══ WHY THE COUNTDOWN WAS INACCURATE ══════════════════════════════════════
 * "~10m left" was `expected_duration_seconds - elapsedSeconds`. Both halves
 * were wrong for an individual run:
 *
 *   • expected_duration_seconds is a per-ACTION POPULATION CONSTANT
 *     (ACTION_EXPECTED_SECONDS, partial_week_replan = 600, measured p50=603 /
 *     p90=650). It is a good average and a category error as a per-run
 *     promise — by construction ~half of all runs blow through it to 0.
 *   • elapsedSeconds was anchored to `mountTimeRef = useRef(Date.now())`, i.e.
 *     COMPONENT MOUNT, while the server's real `started_at` was already on the
 *     wire and already typed and simply never read. Any remount — backgrounding
 *     the app, navigating away and back — replayed "~10m left" from the top on
 *     a run that was nearly done.
 *
 * ══ ⚠️ THE ANCHOR THAT LOOKS RIGHT AND IS NOT ═════════════════════════════
 * `stage_index` / `stage_total` reads like the honest fraction and is NOT
 * usable as the primary signal. `stage_index` is a bare COUNTER incremented in
 * `_tick_cb`, reachable only from `result.tick_stage()` — and there is EXACTLY
 * ONE tick_stage call per action branch, placed AFTER the mutation lands
 * ("Workout created", "Plan rebuilt", "Updating plan..."). For a week replan N
 * goes 0 → 1 at the moment the work FINISHES. A bar built on it would sit at
 * 0% for ten minutes and then jump to 25% as the strip disappeared — worse
 * than the spinner it replaced. weekly_plan_recovery has stage_total = 4 and
 * ZERO tick_stage calls, so its N is NULL forever.
 *
 * `stage_total` is separately unreliable: it is persisted as `len(stages)` at
 * chat_recalibration_orchestrator.py:8156, and `stages` is then TRUNCATED to
 * `stages[:1]` at :8318 for exactly the long actions, and filtered again at
 * :8358 for tiers without watch push. M is written before the list it measures
 * is cut. So it is taken here only as a monotonic FLOOR (honest when it moves —
 * it means a real mutation landed) and never as the denominator.
 *
 * ══ THE ANCHOR THAT IS REAL ═══════════════════════════════════════════════
 * The `substatus` stream. It is the signal the backend deliberately produces —
 * the orchestrator's own comment at :8318 says the handler "takes over from
 * there with real phase updates" — and it flows the whole run, every ≤60s,
 * through the one function every progress path already calls. Its labels are
 * hardcoded and enumerable at dispatch, so they can be mapped to a position.
 *
 * ══ WHY POSITIONS ARE WEIGHTED, NOT EVEN ══════════════════════════════════
 * "Generating this week" is ~500s of a ~600s replan — the gpt-5.2 synthesis,
 * which re-pings its own label every 60s purely to prove liveness. Six evenly
 * spaced stops would put the bar at 50% and freeze it there for eight minutes,
 * which is the stalled-at-90% pathology in a new costume. Each stop therefore
 * carries the fraction of the bar it OWNS.
 *
 * ══ THE LIQUID-LOADER CONTRACT ════════════════════════════════════════════
 * Three decoupled layers; this module is layer 1 only.
 *   1. TRUTH  — where the run actually is (here).
 *   2. EASE   — a display follower chasing truth, so a stage landing glides
 *               instead of teleporting (the component's Animated.Value).
 *   3. SHIMMER— a highlight sweeping the fill, so a momentarily-static bar
 *               still reads as alive (the component).
 * Perceptual contract: MONOTONIC, never reaches 100% before completion, never
 * freezes. Within a stage the value approaches the next stop ASYMPTOTICALLY —
 * it can get arbitrarily close and can never claim a stage that has not landed.
 */

/** A stop in an action's substatus sequence. `at` is where the stage BEGINS. */
export interface RecalStage {
  label: string;
  at: number;
}

/**
 * ══ 2026-09-17 — THE ANCHORS ARE MEASURED NOW ═══════════════════════════════
 * The table below (RECAL_STAGE_SEQUENCES) and the `expected_duration_seconds`
 * constant that paced it were typed by hand from June measurements on gpt-5.2
 * (a week replan ≈ 600 s). The planner moved to gpt-5.6-terra in July and the
 * same replan now completes in ~150 s, so the bar crawled to ~50% and leapt to
 * done — the countdown's inaccuracy in a new costume. A constant cannot follow
 * a model change, and nothing here could see it happen.
 *
 * The server now serves a `progress_plan` on the tracking row: per-stage
 * expected seconds learned from real completions per action + planner model
 * (services/recal_progress_pacing.py, an EWMA of ln(seconds), seeded from
 * production). When a plan with stages is present, THIS module positions the
 * fill from the plan's shares and the table below is not consulted. The table
 * survives only as the fallback for a backend with the flag off or a row
 * written before the plan existed.
 *
 * The plan path is a MIRROR of `advance_plan_state` in that Python module —
 * constants, formulas, evaluation order. `tests/gate/fixtures/
 * recal_progress_parity_vectors.json` is generated FROM the Python side and
 * replayed here, so the two reducers cannot drift. Change both or neither.
 *
 * Two holds were added to BOTH paths:
 *   - no `primaryAction` = dispatch metadata not written yet → the bar HOLDS.
 *     Previously the unmapped fallback ran a 30 s creep toward 92% during that
 *     window, so a strip mounting 20 s into a run painted its first visible
 *     frame at ~70% and, being monotonic, froze there.
 *   - substatus 'clarification_needed' = the user's turn → HOLD (the strip is
 *     hidden in that state; the reducer must not advance underneath it).
 *
 * And the creep inside a stage is two-regime (segmentProgress): a fast
 * exponential that covers ~83% of the span by the stage's expected time, plus
 * a logarithmic tail that keeps the fill visibly moving on a slow run — ~90%
 * at 2×, ~94% at 4×, still moving at 8×, never past SEGMENT_APPROACH_MAX. The
 * shipped single-tau creep reached its cap by ~2× expected and parked there.
 */
export interface RecalStagePlanStage {
  label: string;
  expected_seconds: number;
}

export interface RecalStagePlan {
  version?: number;
  source?: string;
  expected_seconds?: number;
  stages?: RecalStagePlanStage[] | null;
}

/** MIRRORED in services/recal_progress_pacing.py — change both or neither. */
export const CREEP_FAST_SHARE = 0.82;
export const CREEP_FAST_RATE = 3.0;
export const CREEP_TAIL_SHARE = 0.16;
export const CREEP_TAIL_HORIZON = 8.0;
export const MIN_STAGE_SECONDS = 0.5;

/**
 * Per-action substatus sequences, in emission order, with the share of the bar
 * each stage owns. Every label below is quoted from source — none are invented:
 *
 *   "Analyzing changes..." / "Adjusting workout..." / "Generating workout..."
 *   "Rebuilding framework..." / "Updating plan..." / "Syncing to devices..."
 *        → chat_recalibration_orchestrator.py (update_recalibration_substatus)
 *   "Reading your training history" / "Reviewing your framework"
 *   "Generating this week" / "Balancing the week"
 *        → weekly_plan_synthesizer.py:17074, 17094, 18042, 18080
 *   "Reading your context…" / "Designing your session…" / "Finalizing your workout…"
 *        → weekly_plan_synthesizer.py:16313, 16712, 16792 (single-session path)
 *   "Analyzing your goal" / "Designing your framework" / "Sequencing phases"
 *        → goal_framework_generator.py:1698, 1708, 1787
 *
 * Only the LONG actions are mapped. Everything else is ≤15s expected, where a
 * plain time creep is indistinguishable from a staged one and a sequence is
 * just something else to drift. An unmapped action falls back cleanly.
 */
export const RECAL_STAGE_SEQUENCES: Record<string, RecalStage[]> = {
  // ~600s. The synthesis owns 0.22 → 0.86.
  partial_week_replan: [
    { label: 'Analyzing changes', at: 0.0 },
    { label: 'Reading your training history', at: 0.06 },
    { label: 'Reviewing your framework', at: 0.14 },
    { label: 'Generating this week', at: 0.22 },
    { label: 'Balancing the week', at: 0.86 },
    { label: 'Updating plan', at: 0.93 },
  ],
  // ~600s, same synthesizer path — entered directly, no "Analyzing changes".
  weekly_plan_recovery: [
    { label: 'Rebuilding your plan', at: 0.0 },
    { label: 'Reading your training history', at: 0.05 },
    { label: 'Reviewing your framework', at: 0.12 },
    { label: 'Generating this week', at: 0.2 },
    { label: 'Balancing the week', at: 0.84 },
    { label: 'Updating plan', at: 0.92 },
  ],
  // ~660s. Framework regen, then the SAME synthesizer cascade behind it.
  adjust_framework: [
    { label: 'Analyzing changes', at: 0.0 },
    { label: 'Rebuilding framework', at: 0.04 },
    { label: 'Analyzing your goal', at: 0.08 },
    { label: 'Designing your framework', at: 0.14 },
    { label: 'Sequencing phases', at: 0.22 },
    { label: 'Reading your training history', at: 0.3 },
    { label: 'Reviewing your framework', at: 0.35 },
    { label: 'Generating this week', at: 0.4 },
    { label: 'Balancing the week', at: 0.86 },
    { label: 'Framework rebuilt', at: 0.92 },
    { label: 'Syncing to devices', at: 0.96 },
  ],
  // ~75s. synthesize_single_session owns the middle.
  add_workout: [
    { label: 'Analyzing changes', at: 0.0 },
    { label: 'Generating workout', at: 0.08 },
    { label: 'Reading your context', at: 0.16 },
    { label: 'Designing your session', at: 0.3 },
    { label: 'Finalizing your workout', at: 0.78 },
    { label: 'Syncing to devices', at: 0.88 },
    { label: 'Workout created', at: 0.95 },
  ],
  modify_workout: [
    { label: 'Analyzing changes', at: 0.0 },
    { label: 'Adjusting workout', at: 0.08 },
    { label: 'Reading your context', at: 0.16 },
    { label: 'Designing your session', at: 0.3 },
    { label: 'Finalizing your workout', at: 0.78 },
    { label: 'Syncing to devices', at: 0.88 },
    { label: 'Workout modified', at: 0.95 },
  ],
};

// adjust_goal cascades through the identical waterfall as adjust_framework.
RECAL_STAGE_SEQUENCES.adjust_goal = RECAL_STAGE_SEQUENCES.adjust_framework;

/** Ceiling while the run is live. Only `completed` may reach 1. */
export const RUNNING_CEILING = 0.97;
/** Ceiling for a run with no sequence — an honest indeterminate creep. */
export const UNMAPPED_CEILING = 0.92;
/** Fallback pacing when the server sends no estimate. */
export const FALLBACK_EXPECTED_SECONDS = 30;
/**
 * The most of a segment the creep may ever cover. "Asymptotic" is only
 * asymptotic in real arithmetic: `1 - Math.exp(-t/tau)` returns EXACTLY 1 in
 * float64 once t/tau passes ~745, so an unbounded creep does eventually
 * arrive at the next stop and claim a stage that has not landed. This is the
 * hard margin that keeps the promise true at every t.
 */
export const SEGMENT_APPROACH_MAX = 0.985;

/**
 * Labels are compared loosely on purpose. The same stage is written with
 * "..." in Python and "…" in the synthesizer, and casing drifts between
 * writers. Matching on a normalized form means a punctuation change at one of
 * a dozen call sites cannot silently unmap a stage and flatten the bar.
 */
export function normalizeStageLabel(label: string | null | undefined): string {
  if (!label) return '';
  return label
    .toLowerCase()
    .replace(/[.…]+\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface RecalProgressInput {
  primaryAction?: string | null;
  substatus?: string | null;
  stageIndex?: number | null;
  stageTotal?: number | null;
  /** Seconds since the server's started_at — NOT since component mount. On the
   *  plan path the component passes seconds since the server's
   *  dispatch_started_at instead (the tracking row's started_at is the confirm
   *  turn's REASONER start, 14–43 s earlier). */
  elapsedSeconds: number;
  expectedSeconds?: number | null;
  status?: string | null;
  /** The measured stage plan from the tracking row. Stages present → plan path. */
  stagePlan?: RecalStagePlan | null;
  /** Seconds since the server's substatus_changed_at — the exact segment clock,
   *  remount-safe. Null → the local clock since the label was first seen. */
  stageElapsedSeconds?: number | null;
}

export interface RecalProgressState {
  /** Last emitted fraction, 0..1. Monotonic by construction. */
  fraction: number;
  /** Normalized label the current segment began on (null = no stage matched). */
  stageLabel: string | null;
  /** Value of elapsedSeconds when the current segment began. */
  stageStartedAt: number;
  /** Floor and ceiling of the current segment. */
  floor: number;
  ceiling: number;
}

export function initialRecalProgressState(): RecalProgressState {
  return { fraction: 0, stageLabel: null, stageStartedAt: 0, floor: 0, ceiling: UNMAPPED_CEILING };
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * Share of a segment covered after `t` seconds of a stage expected to take
 * `expectedSeconds`. MIRRORED in services/recal_progress_pacing.py.
 */
export function segmentProgress(t: number, expectedSeconds: number): number {
  const e = Math.max(1, Number.isFinite(expectedSeconds) ? expectedSeconds : 1);
  const tt = Math.max(0, Number.isFinite(t) ? t : 0);
  const fast = CREEP_FAST_SHARE * (1 - Math.exp((-CREEP_FAST_RATE * tt) / e));
  const tail = (CREEP_TAIL_SHARE * Math.log(1 + tt / e)) / Math.log(1 + CREEP_TAIL_HORIZON);
  return Math.min(SEGMENT_APPROACH_MAX, fast + tail);
}

export interface PlanPosition {
  label: string;
  floor: number;
  ceiling: number;
  expected: number;
}

/**
 * Each stage's [floor, ceiling] on the bar = its share of the summed expected
 * seconds, scaled to RUNNING_CEILING. MIRRORED in the Python module.
 */
export function planPositions(plan: RecalStagePlan | null | undefined): PlanPosition[] {
  const cleaned: Array<[string, number]> = [];
  for (const s of plan?.stages || []) {
    if (!s || typeof s !== 'object') continue;
    const label = normalizeStageLabel(s.label);
    const raw = Number(s.expected_seconds);
    if (!label || !Number.isFinite(raw)) continue;
    cleaned.push([label, Math.max(MIN_STAGE_SECONDS, raw)]);
  }
  let total = 0;
  for (const [, e] of cleaned) total += e;
  if (cleaned.length === 0 || total <= 0) return [];
  const out: PlanPosition[] = [];
  let cum = 0;
  for (const [label, e] of cleaned) {
    const floor = RUNNING_CEILING * (cum / total);
    cum += e;
    const ceiling = RUNNING_CEILING * (cum / total);
    out.push({ label, floor, ceiling, expected: e });
  }
  return out;
}

/** The plan path. Mirrors `advance_plan_state` in services/recal_progress_pacing.py. */
function advanceOnPlan(
  prev: RecalProgressState,
  input: RecalProgressInput,
  positions: PlanPosition[],
): RecalProgressState {
  const el = Math.max(0, Number.isFinite(input.elapsedSeconds) ? input.elapsedSeconds : 0);
  const wanted = normalizeStageLabel(input.substatus);
  let idx = -1;
  for (let i = 0; i < positions.length; i += 1) {
    if (positions[i].label === wanted) idx = i;
  }
  let seg: PlanPosition;
  if (idx === -1) {
    // A label the plan does not know. Mid-run it holds the current segment; at
    // the very start (a reasoner-authored first stage the estimator has not yet
    // folded) it creeps inside the FIRST segment rather than at zero.
    if (prev.stageLabel !== null) return prev;
    seg = positions[0];
  } else {
    seg = positions[idx];
  }
  const label = wanted;
  const se = input.stageElapsedSeconds;
  let t: number;
  if (se != null && Number.isFinite(se) && se >= 0) {
    t = se;
  } else if (prev.stageLabel === label) {
    t = Math.max(0, el - prev.stageStartedAt);
  } else {
    t = 0;
  }
  const stageStartedAt = el - t;
  const span = Math.max(0, seg.ceiling - seg.floor);
  let fraction = seg.floor + span * segmentProgress(t, seg.expected);
  fraction = Math.min(fraction, RUNNING_CEILING);
  fraction = Math.max(prev.fraction, clamp01(fraction));
  return { fraction, stageLabel: label, stageStartedAt, floor: seg.floor, ceiling: seg.ceiling };
}

/** Resolve where a substatus sits in its action's sequence. */
function resolveSegment(
  input: RecalProgressInput,
): { floor: number; ceiling: number; label: string } | null {
  const action = (input.primaryAction || '').trim();
  const seq = RECAL_STAGE_SEQUENCES[action];
  if (!seq || seq.length === 0) return null;

  const wanted = normalizeStageLabel(input.substatus);
  if (!wanted) return null;

  // Last match wins: a label that legitimately repeats later in a sequence
  // should resolve to its later position, never walk the bar backwards.
  let idx = -1;
  for (let i = 0; i < seq.length; i += 1) {
    if (normalizeStageLabel(seq[i].label) === wanted) idx = i;
  }
  if (idx === -1) return null;

  const floor = seq[idx].at;
  const ceiling = idx + 1 < seq.length ? seq[idx + 1].at : RUNNING_CEILING;
  return { floor, ceiling, label: wanted };
}

/**
 * Advance the bar. Pure: same (prev, input) always yields the same state.
 *
 * ⚠️ THE SEGMENT CLOCK RESETS ONLY WHEN THE LABEL CHANGES. The synthesizer
 * re-emits "Generating this week" every 60s from `_heartbeat_ping` purely to
 * prove the gpt-5.2 call is still alive. Resetting on every substatus WRITE
 * would restart the creep every minute and saw the bar backwards for eight
 * straight minutes on the single longest stage in the product.
 */
export function advanceRecalProgress(
  prev: RecalProgressState,
  input: RecalProgressInput,
): RecalProgressState {
  const status = (input.status || '').trim();
  if (status === 'completed') {
    return { ...prev, fraction: 1, floor: 1, ceiling: 1 };
  }
  // Dispatch metadata not written yet → nothing is progressing → hold. This is
  // what stops the first visible frame landing at ~70%: see the header.
  if (!(input.primaryAction || '').trim()) return prev;
  // The user's turn. The strip is hidden in this state; the reducer must not
  // advance underneath it.
  if (normalizeStageLabel(input.substatus) === 'clarification_needed') return prev;

  const positions = planPositions(input.stagePlan);
  if (positions.length > 0) return advanceOnPlan(prev, input, positions);

  const elapsed = Number.isFinite(input.elapsedSeconds) ? Math.max(0, input.elapsedSeconds) : 0;
  const expected =
    input.expectedSeconds != null && input.expectedSeconds > 0
      ? input.expectedSeconds
      : FALLBACK_EXPECTED_SECONDS;

  const seg = resolveSegment(input);

  let floor: number;
  let ceiling: number;
  let stageLabel: string | null;
  let stageStartedAt: number;

  if (seg) {
    stageLabel = seg.label;
    floor = seg.floor;
    ceiling = seg.ceiling;
    if (prev.stageLabel === seg.label) {
      // Same stage as last tick → keep its clock running.
      stageStartedAt = prev.stageStartedAt;
    } else if (prev.stageLabel === null && elapsed > 0) {
      // COLD MOUNT MID-RUN. We have no memory of when this stage began, and
      // seeding the clock at `elapsed` would restart the creep from the stage
      // FLOOR — the user backgrounds the app at 80% and returns to 22%. That
      // is the remount defect this bar exists to retire, re-entering through
      // the back door. The sequence's own `at` values ARE fractions of the
      // expected duration, so `floor * expected` is the model's estimate of
      // when this stage started; clamped so the clock can never run negative.
      stageStartedAt = Math.min(elapsed, floor * expected);
    } else {
      // A real stage change we witnessed → restart the clock here.
      stageStartedAt = elapsed;
    }
  } else {
    // Unmapped action, or a label this sequence does not know. Hold the segment
    // we were already in rather than collapsing to zero — an unrecognized label
    // is missing information, not evidence of regress.
    stageLabel = prev.stageLabel;
    floor = prev.stageLabel === null ? 0 : prev.floor;
    ceiling = prev.stageLabel === null ? UNMAPPED_CEILING : prev.ceiling;
    stageStartedAt = prev.stageStartedAt;
  }

  // Asymptotic creep across the segment. tau is half the segment's expected
  // wall time, so at the expected duration the bar has covered ~86% of the
  // span and keeps decelerating — it approaches the next stop and never
  // arrives, which is exactly the promise "a stage has not landed yet".
  const span = Math.max(0, ceiling - floor);
  const segmentExpected = Math.max(1, span * expected);
  const tau = Math.max(1, segmentExpected * 0.5);
  const t = Math.max(0, elapsed - stageStartedAt);
  const eased = Math.min(SEGMENT_APPROACH_MAX, 1 - Math.exp(-t / tau));

  let fraction = floor + span * eased;

  // stage_index is honest WHEN IT MOVES — it means a real mutation landed — so
  // it is taken as an extra floor. Never as the denominator: see the header.
  const si = input.stageIndex;
  const st = input.stageTotal;
  if (si != null && st != null && st > 0 && si > 0) {
    fraction = Math.max(fraction, Math.min(si, st) / st);
  }

  fraction = Math.min(fraction, RUNNING_CEILING);
  fraction = Math.max(prev.fraction, clamp01(fraction)); // monotonic

  return { fraction, stageLabel, stageStartedAt, floor, ceiling };
}
